import { arrowRight } from "@simplybusiness/icons";
import { Box, Container, Flex, Image, Text } from "@simplybusiness/mobius";
import type { ShowcaseProps } from "../../pages/ShowcasePage/types";
import { useShowcaseReveal } from "./useShowcaseReveal";
import { Disclaimers } from "./Disclaimers";
import { ShowcaseEmbeddedQuickReplies } from "./ShowcaseEmbeddedQuickReplies";
import { ShowcaseExploreOptions } from "./ShowcaseExploreOptions";
import { ShowcaseForm } from "./ShowcaseForm";
import { ShowcasePriceBlock } from "./ShowcasePriceBlock";
import { ShowcaseProductsListTreatmentEmbeddedQuickReplies } from "./ShowcaseProductsListTreatmentEmbeddedQuickReplies";
import "./ShowcaseTreatmentEmbeddedQuickReplies.css";

const ACCEPT_SHOWCASE_ACTION = "Accept showcase";
const DECLINE_SHOWCASE_ACTION = "Decline showcase";

export const ShowcaseTreatmentEmbeddedQuickReplies = ({
  showcaseName,
  coverageText,
  targetAudienceText,
  showcaseProducts,
  actions,
  offerUuid,
  showcaseAccepted,
  tradeImage,
  recommendations,
  disclaimers,
}: ShowcaseProps) => {
  useShowcaseReveal();

  const submitAction = actions.find(
    action =>
      action.actionType === "submit" && action.name === ACCEPT_SHOWCASE_ACTION,
  );

  const declineAction = actions.find(
    action =>
      action.actionType === "submit" && action.name === DECLINE_SHOWCASE_ACTION,
  );

  const backAction = actions.find(action => action.actionType === "back");

  return (
    <Container size="sm">
      <Text
        elementType="h2"
        className="mobius-section__title mobius-showcase-heading"
      >
        Recommended for you
      </Text>
      <Box className="mobius-showcase__layout mobius-showcase__layout--with-faq">
        <Box className="mobius-showcase__card mobius-showcase__card--with-faq">
          <Box className="mobius-showcase__card-content">
            <Flex
              className="mobius-showcase__content"
              flexDirection="column"
              gap="md"
            >
              <Box>
                <Text elementType="h2" className="mobius-showcase__title">
                  {showcaseName}
                </Text>
                <ShowcasePriceBlock showcaseProducts={showcaseProducts} />
                <Text className="mobius-showcase__coverage">
                  {coverageText}
                </Text>
                {targetAudienceText && (
                  <Text className="mobius-showcase__audience">
                    {targetAudienceText}
                  </Text>
                )}
              </Box>
              <Box>
                <p className="mobius-showcase__heading">What&#39;s included:</p>
                <ShowcaseProductsListTreatmentEmbeddedQuickReplies
                  products={showcaseProducts}
                />
              </Box>
              {submitAction && (
                <ShowcaseForm
                  action={submitAction}
                  className="mobius-showcase-form__accept-showcase-cta"
                  icon={arrowRight}
                  hiddenInputs={[
                    { name: "showcase_accepted", value: showcaseAccepted },
                    { name: "offer_uuid", value: offerUuid },
                  ]}
                />
              )}
            </Flex>
            <Box
              className="mobius-showcase__image-wrapper"
              data-testid="showcase-image-wrapper"
            >
              <Image
                alt={`${showcaseName} trade image`}
                className="mobius-showcase__image"
                src={tradeImage}
              />
            </Box>
          </Box>
          <Box className="mobius-showcase__faq-section">
            <ShowcaseEmbeddedQuickReplies
              title="Frequently asked questions"
              inputPlaceholder="Write your question"
              product={showcaseProducts[0]}
            />
          </Box>
        </Box>
        {declineAction && (
          <Box
            className="mobius-showcase-explore"
            data-testid="showcase-explore-options"
          >
            <ShowcaseExploreOptions
              recommendations={recommendations}
              declineAction={declineAction}
            />
          </Box>
        )}
      </Box>
      <Disclaimers disclaimers={disclaimers} />
      {backAction && (
        <ShowcaseForm
          action={backAction}
          buttonVariant="secondary"
          className="mobius-showcase-form__back"
          buttonClassName="mobius-showcase-back__button"
        />
      )}
    </Container>
  );
};
